import { FC, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Button, TextField } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { CustomSelectFormik } from 'components/elements';
import { IOption } from 'components/elements/CustomSelect';
import toastService from 'components/elements/Toastify';
import { useAppSelector } from 'store/hooks';
import { getStreetTypeOptionsSelector } from 'store/streets-types';
import { IErrorResponse, IUtilityProviderDetails, IUtilityProvidersResponse } from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { getErrorMessage } from 'utils/helpers';
import { ProvidersService } from 'utils/services';
import { CityAutocompleteField } from '../CityAutocompleteField';
import s from './index.module.scss';

const DEVICE_FP = '6037e65eefbdasdfadsfasdf3';

const normalizeUtilityProviders = (response: IUtilityProvidersResponse): IUtilityProviderDetails[] =>
	Array.isArray(response) ? response : response.results || [];

export interface IAliasFormValues {
	name: string;
	streetTypeId: string;
	streetId: string;
	cityId: string;
	providerId: string;
}

interface AliasFormProps {
	buttonText: string;
	cityOptions: IOption[];
	cityOptionsLoading: boolean;
	hasMoreCityOptions: boolean;
	initialCityTitle?: string;
	initialValues: IAliasFormValues;
	onCityLoadMore: () => void;
	onCitySearch: (search: string) => void;
	onSubmit: (values: IAliasFormValues) => void;
}

export const AliasForm: FC<AliasFormProps> = ({
	buttonText,
	cityOptions,
	cityOptionsLoading,
	hasMoreCityOptions,
	initialCityTitle = '',
	initialValues,
	onCityLoadMore,
	onCitySearch,
	onSubmit,
}) => {
	const streetTypeOptions = useAppSelector(getStreetTypeOptionsSelector);
	const [providerOptions, setProviderOptions] = useState<{ title: string; value: string }[]>([]);

	useEffect(() => {
		const loadUtilityProviders = async () => {
			try {
				const response = await ProvidersService.getUtilityProviders({
					offset: 0,
					limit: PAGINATION_LIMIT.BIG,
					deviceFP: DEVICE_FP,
				});
				setProviderOptions(
					normalizeUtilityProviders(response.data).map(({ id, name }) => ({
						title: name,
						value: `${id}`,
					}))
				);
			} catch (error) {
				toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
			}
		};

		void loadUtilityProviders();
	}, []);

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit}>
			{({ values, setFieldValue }) => {
				const isSubmitDisabled = !values.name.trim();

				return (
					<Form className={s.form}>
						<Field label="Назва вулиці" name="name" variant="outlined" as={TextField} fullWidth />
						<Field label="Тип вулиці" name="streetTypeId" options={streetTypeOptions} component={CustomSelectFormik} />
						<Field label="Street ID" name="streetId" variant="outlined" as={TextField} fullWidth />
						<CityAutocompleteField
							cityOptions={cityOptions}
							cityOptionsLoading={cityOptionsLoading}
							hasMoreCityOptions={hasMoreCityOptions}
							initialCityTitle={initialCityTitle}
							onCityLoadMore={onCityLoadMore}
							onCitySearch={onCitySearch}
							onChange={(cityId) => setFieldValue('cityId', cityId)}
							value={values.cityId}
						/>
						<Field label="Провайдер" name="providerId" options={providerOptions} component={CustomSelectFormik} />

						<Button className={s.button} type="submit" disabled={isSubmitDisabled} variant="rounded">
							{buttonText}
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};
